import {
	HttpErrorResponse,
	HttpEvent,
	HttpHandler,
	HttpInterceptor,
	HttpRequest,
} from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { BehaviorSubject, Observable, throwError } from "rxjs";
import { catchError, filter, switchMap, take } from "rxjs/operators";

import { AuthTokenType } from "../auth/models/auth-token-type.enum";
import { AuthService } from "../auth/services/auth.service";
import { TranslationService } from "../services";
import { AlertNewSnakbarService } from "../services/alert-new-snakbar.service";
import { TokenRefreshService } from "../auth/services/token-refresh.service";
import { ApiMessageType } from "../types/api-message.type";
import { APP_CONFIG, IAppConfig } from "../config";
import { GenericHubService } from "@app/shared/services/signalR/generic-hub.service";

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
	private authorizationHeader = "Authorization";
	private currentLang: string;

	private isLoggingOut = false;

	private isRefreshing = false;
	private isConnectionErrorShown = false;
	private errorStack: any[] = [];

	private refreshTokenSubject: BehaviorSubject<string | null> =
		new BehaviorSubject<string | null>(null);

	constructor(
		@Inject(APP_CONFIG) private appConfig: IAppConfig,
		private authService: AuthService,
		private alertNewSnakbarService: AlertNewSnakbarService,
		private translationService: TranslationService,
		private router: Router,
		private tokenRefreshService: TokenRefreshService,
		private genericHubService: GenericHubService
	) {
		this.currentLang =
			this.translationService.getCurrentLang().uniqueSeoCode;
	}

	intercept(
		request: HttpRequest<any>,
		next: HttpHandler
	): Observable<HttpEvent<any>> {
		const accessToken = this.authService.getRawAuthToken(
			AuthTokenType.AccessToken
		);

		if (this.isLoggingOut) {
			const plainReq = this.addCommonHeaders(
				request,
				accessToken || undefined
			);
			return next.handle(plainReq);
		}

		if (!accessToken) {
			const anonRequest = this.addCommonHeaders(request);
			return next.handle(anonRequest).pipe(
				catchError((error: HttpErrorResponse) => {
					if (error.status === 401) {
						this.forceLogout();
					} else {
						this.handleError(error);
					}
					return throwError(() => error);
				})
			);
		}

		const authRequest = this.addCommonHeaders(request, accessToken);

		return next.handle(authRequest).pipe(
			catchError((error: HttpErrorResponse) => {
				if (error.status === 401) {
					if (this.isRefreshRequest(request)) {
						this.forceLogout();
						return throwError(() => error);
					}

					if (this.isLoggingOut) {
						return throwError(() => error);
					}
					return this.handle401Error(authRequest, next);
				}

				this.handleError(error);
				return throwError(() => error);
			})
		);
	}

	// ----------------- 401 / Refresh Token -----------------

	private handle401Error(
		request: HttpRequest<any>,
		next: HttpHandler
	): Observable<HttpEvent<any>> {
		if (!this.isRefreshing) {
			this.isRefreshing = true;
			this.refreshTokenSubject.next(null);

			return this.tokenRefreshService.handleRefreshToken().pipe(
				switchMap((result: any) => {
					this.isRefreshing = false;

					this.authService.setLoginSession(result);

					const newToken = this.authService.getRawAuthToken(
						AuthTokenType.AccessToken
					);
					if (!newToken) {
						this.forceLogout();
						return throwError(
							() => new Error("No new access token after refresh")
						);
					}

					this.refreshTokenSubject.next(newToken);
					this.genericHubService.reconnectWithNewToken();
					const newRequest = this.addCommonHeaders(request, newToken);
					return next.handle(newRequest);
				}),
				catchError((err: any) => {
					this.isRefreshing = false;
					this.refreshTokenSubject.next(null);
					this.forceLogout();
					return throwError(() => err);
				})
			);
		}

		return this.refreshTokenSubject.pipe(
			filter((token) => token !== null),
			take(1),
			switchMap((token: string | null) => {
				const newRequest = this.addCommonHeaders(request, token!);
				return next.handle(newRequest);
			})
		);
	}

	private forceLogout(): void {
		if (this.isLoggingOut) {
			return;
		}

		this.isLoggingOut = true;
		this.authService.logout(true);
		this.authService.deleteAuthTokens();
		this.router.navigate(["/auth"]);
	}

	private isRefreshRequest(request: HttpRequest<any>): boolean {
		const refreshPath = this.appConfig.refreshTokenPath;

		return Boolean(
			refreshPath &&
				request.url.toLowerCase().includes(refreshPath.toLowerCase())
		);
	}

	// ----------------- Header helpers -----------------

	private addCommonHeaders(
		request: HttpRequest<any>,
		accessToken?: string
	): HttpRequest<any> {
		const setHeaders: { [name: string]: string } = {};

		if (!request.headers.has("lang")) {
			setHeaders["lang"] = this.currentLang;
		}

		if (!request.headers.has("language")) {
			setHeaders["language"] = this.currentLang;
		}

		if (!this.isFormData(request) && !request.headers.has("Content-Type")) {
			setHeaders["Content-Type"] = "application/json";
		}

		if (accessToken) {
			setHeaders[this.authorizationHeader] = `Bearer ${accessToken}`;
		}

		if (!request.headers.has("X-Origin")) {
			setHeaders["X-Origin"] = "3";
		}

		if (!request.headers.has("Cache-Control")) {
			setHeaders["Cache-Control"] = "no-cache";
		}

		if (Object.keys(setHeaders).length > 0) {
			request = request.clone({ setHeaders });
		}

		return request;
	}

	// ----------------- Error handling -----------------

	private handleError(error: HttpErrorResponse): void {
		const url = (error.url || "").toLowerCase();

		if (
			(error.status === 0 ||
				error.status === 408 ||
				error.status === 499) &&
			!url.endsWith("/notification/notify")
		) {
			this.showConnectionError(error);
			return;
		}

		switch (error.status) {
			case 400:
				this.handleShowErrors(error, "AUTH.ERROR_400");
				break;

			case 403:
				this.router.navigate(["/forbidden"]);
				break;

			case 404:
				this.handleShowErrors(error, "AUTH.ERROR_404");
				break;

			case 424:
				this.handleShowErrors(error, "");
				break;

			case 500:
				this.alertNewSnakbarService.snackBarError("AUTH.ERROR_500");
				break;

			case 409:
				this.handleShowErrors(error, "AUTH.ERROR_UNKNOWN");
				break;
		}
	}

	private showConnectionError(error: HttpErrorResponse): void {
		this.errorStack.push(error);

		if (this.isConnectionErrorShown) {
			return;
		}

		this.isConnectionErrorShown = true;
		this.alertNewSnakbarService.snackBarError("AUTH.CONNECTION_ERROR");

		setTimeout(() => {
			this.isConnectionErrorShown = false;
			this.errorStack = [];
		}, 5000);
	}

	private isFormData(req: HttpRequest<any>): boolean {
		const url = req.url;

		if (
			url.endsWith("/ticket") ||
			url.endsWith("/ticket/reply") ||
			url.endsWith("/verify/PersonalInformation") ||
			url.endsWith("/verify/Address") ||
			url.endsWith("/Verify/Photo") ||
			url.endsWith("/BankPayment") ||
			url.endsWith("/cashPayment") ||
			url.endsWith("/ServicePayment/Deposit") ||
			url.endsWith("/payments/e-money") ||
			url.endsWith("/Request/message") ||
			url.endsWith("/ServicePayment/Exchange/Pay") ||
			url.endsWith("/ServicePayment/Exchange/Pay/download/") ||
			url.endsWith("/exportexcel") ||
			url.endsWith("/payments/upload") ||
			url.endsWith("/crypto-transaction/message/upload") ||
			url.endsWith("/payments/cash") ||
			url.endsWith("/requests/messages") ||
			url.endsWith("/payments/bank")
		) {
			return true;
		}

		return false;
	}

	private handleShowErrors(
		error: HttpErrorResponse,
		defaultText: string
	): void {
		const backendMessage = this.extractBackendMessage(error);

		if (backendMessage) {
			this.alertNewSnakbarService.snackBarError(backendMessage);
			return;
		}

		if (defaultText) {
			this.alertNewSnakbarService.snackBarError(defaultText);
		}
	}

	private extractBackendMessage(error: HttpErrorResponse): string | null {
		const messages = error?.error?.messages;

		if (Array.isArray(messages) && messages.length > 0) {
			const firstMessage = messages.find(
				(message) => message?.description || message?.title
			);

			return firstMessage?.description || firstMessage?.title || null;
		}

		return (
			error?.error?.error?.details?.[0]?.message ||
			error?.error?.error?.message ||
			error?.error?.error?.code ||
			error?.error?.message ||
			(typeof error?.error === "string" ? error.error : null)
		);
	}

	private handleAlertType(element: any, description: string) {
		switch (element.type) {
			case ApiMessageType.Error:
				this.alertNewSnakbarService.snackBarError(description);
				break;
			case ApiMessageType.Successful:
				this.alertNewSnakbarService.snackBarSuccess(description);
				break;
			case ApiMessageType.Warning:
				this.alertNewSnakbarService.snackBarWarning(description);
				break;
			default:
				this.alertNewSnakbarService.snackBarInfo(description);
				break;
		}
	}
}
